import { useEffect, useRef, type ClipboardEvent, type KeyboardEvent } from "react";
import clsx from "clsx";

export interface OtpCodeInputProps {
  length?: number;
  value: string;
  onChange: (value: string) => void;
  disabled?: boolean;
  autoFocus?: boolean;
  id?: string;
  "data-testid"?: string;
}

export function OtpCodeInput({
  length = 6,
  value,
  onChange,
  disabled = false,
  autoFocus = false,
  id,
  "data-testid": testId,
}: OtpCodeInputProps) {
  const inputRefs = useRef<(HTMLInputElement | null)[]>([]);
  const digits = Array.from({ length }, (_, index) => value[index] ?? "");

  const focusInput = (index: number) => {
    inputRefs.current[index]?.focus();
    inputRefs.current[index]?.select();
  };

  const applyDigits = (next: string, focusIndex?: number) => {
    const normalized = next.replace(/\D/g, "").slice(0, length);
    onChange(normalized);
    if (focusIndex !== undefined) {
      focusInput(Math.min(Math.max(focusIndex, 0), length - 1));
    }
  };

  useEffect(() => {
    if (autoFocus) {
      focusInput(0);
    }
  }, [autoFocus]);

  const handleInput = (index: number, raw: string) => {
    const cleaned = raw.replace(/\D/g, "");
    if (!cleaned) {
      const chars = digits.slice();
      chars[index] = "";
      applyDigits(chars.join(""));
      return;
    }

    if (cleaned.length === 1) {
      const chars = digits.slice();
      chars[index] = cleaned;
      applyDigits(chars.join(""), index < length - 1 ? index + 1 : index);
      return;
    }

    const prefix = digits.slice(0, index).join("");
    applyDigits(prefix + cleaned, Math.min(prefix.length + cleaned.length, length - 1));
  };

  const handleKeyDown = (index: number, event: KeyboardEvent<HTMLInputElement>) => {
    if (event.key === "Backspace" && !digits[index] && index > 0) {
      focusInput(index - 1);
    }
    if (event.key === "ArrowLeft" && index > 0) {
      event.preventDefault();
      focusInput(index - 1);
    }
    if (event.key === "ArrowRight" && index < length - 1) {
      event.preventDefault();
      focusInput(index + 1);
    }
  };

  const handlePaste = (event: ClipboardEvent<HTMLInputElement>) => {
    event.preventDefault();
    const pasted = event.clipboardData.getData("text").replace(/\D/g, "").slice(0, length);
    if (pasted) {
      applyDigits(pasted, Math.min(pasted.length, length - 1));
    }
  };

  return (
    <div className="flex flex-col gap-2" data-testid={testId}>
      <div className="flex justify-center gap-2 sm:gap-3" role="group" aria-label="Authentication code">
        {digits.map((digit, index) => (
          <input
            key={index}
            ref={(element) => {
              inputRefs.current[index] = element;
            }}
            id={index === 0 ? id : undefined}
            type="text"
            inputMode="numeric"
            pattern="[0-9]*"
            autoComplete={index === 0 ? "one-time-code" : "off"}
            maxLength={index === 0 ? length : 1}
            value={digit}
            disabled={disabled}
            onChange={(event) => handleInput(index, event.target.value)}
            onKeyDown={(event) => handleKeyDown(index, event)}
            onPaste={handlePaste}
            onFocus={(event) => event.currentTarget.select()}
            className={clsx(
              "h-12 w-10 sm:h-14 sm:w-12 rounded-xl border-2 text-center text-lg sm:text-xl font-semibold tabular-nums transition-colors",
              "border-gray-200 bg-white text-gray-900",
              "dark:border-gray-600 dark:bg-gray-700 dark:text-gray-100",
              "focus:border-indigo-500 focus:outline-none focus:ring-2 focus:ring-indigo-500/25",
              disabled && "cursor-not-allowed opacity-50",
            )}
            aria-label={`Digit ${index + 1} of ${length}`}
          />
        ))}
      </div>
    </div>
  );
}
